"use client";

import { ArrowUpRight, RefreshCcw } from "lucide-react";
import { Typography } from "@/components/common/ui/Typography";
import { cn } from "@/lib/utils/cn";
import type { BaseCarrier, CarrierLink } from "./types";

/** Carrier logo with placeholder fallback. */
export function CarrierLogo({
  carrier,
  className,
}: {
  carrier: BaseCarrier;
  className?: string;
}) {
  return (
    <div
      className={cn("flex-shrink-0 w-14 h-10 flex items-center justify-center bg-gray-50 rounded-md p-1.5 border border-gray-100", className)}
    >
      <img
        src={carrier.logo}
        alt={carrier.name}
        className="max-w-full max-h-full object-contain"
        onError={(e) => {
          e.currentTarget.src = "/images/carriers/placeholder.svg";
        }}
      />
    </div>
  );
}

/** Carrier name — fixed-width, truncated. */
export function CarrierName({ carrier }: { carrier: BaseCarrier }) {
  return (
    <div className="w-36 flex-shrink-0">
      <Typography
        as="span"
        variant="headline5"
        truncate
        className="block"
      >
        {carrier.name}
      </Typography>
    </div>
  );
}

/** Teal "Portal Link" button. */
export function PortalLinkButton({
  link,
  className,
}: {
  link: CarrierLink;
  className?: string;
}) {
  return (
    <a
      href={link.href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={cn("flex items-center gap-1 px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs font-medium hover:bg-teal-100 transition-colors", className)}
    >
      {link.label ?? "Portal Link"} <ArrowUpRight className="w-3 h-3" />
    </a>
  );
}

/** Vertical divider + refresh icon button for re-rating a carrier. */
export function ReRateButton({
  onReRate,
  disabled = false,
  title,
}: {
  onReRate: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <>
      <div className="flex-shrink-0 w-px h-8 bg-gray-200" />
      <div className="w-10 flex-shrink-0 flex justify-center">
        <button
          onClick={(e) => {
            e.stopPropagation();
            if (disabled) return;
            onReRate();
          }}
          disabled={disabled}
          className={cn(
            "p-1.5 rounded transition-colors",
            disabled
              ? "cursor-not-allowed text-gray-300"
              : "text-gray-400 hover:text-blue-600 hover:bg-blue-50",
          )}
          title={title}
        >
          <RefreshCcw className="w-4 h-4" />
        </button>
      </div>
    </>
  );
}

/** Inline "Re-rate" button shown in the expanded footer at narrow breakpoints. */
export function InlineReRateButton({
  onReRate,
  disabled = false,
  title,
}: {
  onReRate: () => void;
  disabled?: boolean;
  title: string;
}) {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        onReRate();
      }}
      disabled={disabled}
      className={cn(
        "@3xl:hidden flex items-center gap-1.5 px-2 py-1 rounded text-sm font-medium transition-colors",
        disabled
          ? "cursor-not-allowed text-gray-300"
          : "text-gray-500 hover:text-blue-600 hover:bg-blue-50",
      )}
      title={title}
    >
      <RefreshCcw className="w-3.5 h-3.5" />
      Re-rate
    </button>
  );
}
